<template>
  <div
    class="image-card"
    @click="$emit('grid-item-clicked', imageObj)"
    @mouseover="mouseHover = true"
    @mouseleave="mouseHover = false"
  >
    <div class="image-wrapper">

      <div v-if="shouldShowSelector" class="image-card__checkbox" @click.stop="">
        <v-checkbox
        :modelValue="isSelected"
          :hide-details="true"
          density="compact"
          @update:model-value="toggleSelection"
        />
      </div>

      <ImageCanvas class="image-canvas"
        :imageId="imageObj?.id"
        :boxes="boxes"
        :segmentations="segmentations"
        :crop="crop"
        :cropPadding="cropPadding"
        :imageQuality="imageQuality"
        :fetchPriority="fetchPriority"
      />
      <div v-if="showName" class="image-card__name-overlay">
        <p class="image-card__name-text" :title="imageObj.name || 'Unknown'">{{ imageObj.name || 'Unknown' }}</p>
      </div>
    </div>
    <div v-if="showLabels && allLabelNames.length > 0" class="image-card__labels">
      <div class="image-card__tag-list">
        <span
          v-for="label in allLabelNames"
          :key="label.index"
          class="image-card__label-tag"
          :style="tagColor(label.index)"
        >{{ label.name }}</span>
      </div>
    </div>
    <div v-if="showTags && allTagNames.length > 0" class="image-card__tags">
      <span class="image-card__section-title">Tags</span>
      <div class="image-card__tag-list">
        <v-chip
          v-for="tag in allTagNames"
          :key="tag"
          variant="outlined"
          density="compact"
          size="x-small"
        >{{ tag }}</v-chip>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import ImageCanvas from '@/components/ImageCanvas.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import { getColor } from '@/assets/js/utils.js';

const props = defineProps({
  imageObj: {
    type: Object,
    default: () => ({}),
  },
  annotations: {
    type: [Array, Object],
    default: () => [],
  },
  annotationSets: {
    type: Array,
    default: () => [],
  },
  annotationDisplayType: {
    type: String,
    default: 'set',
  },
  labels: {
    type: Array,
    default: () => [],
  },
  crop: {
    type: Object,
    default: null,
  },
  cropPadding: {
    type: Number,
    default: 0,
  },
  imageQuality: {
    type: String,
    default: 'thumbnail',
  },
  fetchPriority: {
    type: String,
    default: 'auto',
  },
  filterAnnotationsBySets: {
    type: Array,
    default: null,
  },
  filterAnnotationsByLabelIndexes: {
    type: Array,
    default: null,
  },
  showName: {
    type: Boolean,
    default: false,
  },
  showTags: {
    type: Boolean,
    default: false,
  },
  showLabels: {
    type: Boolean,
    default: true,
  },
  shouldShowSelector: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['grid-item-clicked', 'update:selected']);

const mouseHover = ref(false);
const isSelected = ref(props.selected);

const { map: annotationSetColorMap } = useAnnotationColorMap({ items: props.annotationSets, key: 'id' });
const { map: labelColorMap } = useAnnotationColorMap({ items: props.labels, key: 'index' });

const filteredAnnotations = computed(() => {
  let annotationsArray;
  if (Array.isArray(props.annotations)) {
    annotationsArray = props.annotations;
  } else if (typeof props.annotations === 'object') {
    annotationsArray = [props.annotations];
  } else {
    annotationsArray = [];
  }

  if (props.filterAnnotationsBySets?.length >= 0) {
    annotationsArray = annotationsArray.filter((a) => props.filterAnnotationsBySets.includes(a.annotation_set_id));
  }
  if (props.filterAnnotationsByLabelIndexes?.length >= 0) {
    annotationsArray = annotationsArray.filter((a) => props.filterAnnotationsByLabelIndexes.includes(a.label_index));
  }
  return annotationsArray;
});

const boxes = computed(() => {
  return filteredAnnotations.value
    .filter((a) => a.type === 'box' || a.type === 'auto-box')
    .map((a) => convertAnnotationToCanvasBox(a));
});

const segmentations = computed(() => {
  return filteredAnnotations.value
    .filter((a) => a.type === 'seg' || a.type === 'auto-seg')
    .map((a) => convertAnnotationToSegmentation(a));
});

const isSequence = computed(() => {
  return props.imageObj.type === 'sequence';
});

const allLabelIndexes = computed(() => {
  return [...new Set(filteredAnnotations.value.map((a) => a.label_index))].sort((a, b) => a - b);
});

const allLabelNames = computed(() => {
  return allLabelIndexes.value.map((index) => {
    const lbl = props.labels.find((l) => l.index === index);
    return lbl || { index, name: index.toString() };
  });
});

const allTagNames = computed(() => {
  const tags = props.imageObj.tags;
  if (tags && tags.length > 0) {
    return tags.map((e) => (typeof e === 'object' ? e.name : e));
  }
  return [];
});

watch(() => props.selected, (newVal) => {
  isSelected.value = newVal;
});

const toggleSelection = () => {
  isSelected.value = !isSelected.value;
  emit('update:selected', isSelected.value);
};

const addOpacityToHSL = (hslString, opacity = 0.2) => {
  if (!hslString) return 'rgba(128, 128, 128, 0.2)'; // fallback color if undefined
  return hslString
    .replace(/^hsl/i, "hsla")
    .replace(")", `, ${opacity})`);
};

const tagColor = (index) => {
  const label = props.labels.find((e) => e.index === index);
  let color;
  
  // Use the same logic as annotations: label color first, then display type logic
  if (label && label.color) {
    color = parseColor(label.color);
  } else if (props.annotationDisplayType === 'set') {
    color = labelColorMap[index];
  } else if (props.annotationDisplayType === 'label') {
    color = getColor(index);
  } else {
    color = labelColorMap[index];
  }
  
  const bgColor = color ? addOpacityToHSL(color, 0.2) : 'rgba(128, 128, 128, 0.2)';
  
  return {
    backgroundColor: bgColor,
    borderColor: color || 'rgba(128, 128, 128, 1)',
    color: 'black',
  };
};

const convertAnnotationToCanvasBox = (anno) => {
  let originX = 0;
  let originY = 0;
  let x = anno.x;
  let y = anno.y;
  const w = anno.w;
  const h = anno.h;
  if (props.annotationSets) {
    const annoTask = props.annotationSets.find((set) => set.id === anno.annotation_set_id);
    if (annoTask) {
      if (annoTask.origin_x) { originX = annoTask.origin_x; x -= originX * anno.w; }
      if (annoTask.origin_y) { originY = annoTask.origin_y; y -= originY * anno.h; }
    }
  }
  const label = props.labels.find((e) => e.name === anno.label_name);
  let color;
  if (label && label.color) {
    color = parseColor(label.color);
  } else if (props.annotationDisplayType === 'set' && anno.annotation_set_id) {
    color = annotationSetColorMap[anno.annotation_set_id];
  } else if (props.annotationDisplayType === 'label' && anno.label_index >= 0) {
    color = getColor(anno.label_index);
  }
  return { x, y, w, h, color, opacity: anno.opacity || 1 };
};

const convertAnnotationToSegmentation = (anno) => {
  const polygons = JSON.parse(anno.polygon);
  const label = props.labels.find((e) => e.name === anno.label_name);
  let color;
  if (label && label.color) {
    color = parseColor(label.color);
  } else if (props.annotationDisplayType === 'set' && anno.annotation_set_id) {
    color = annotationSetColorMap[anno.annotation_set_id];
  } else if (props.annotationDisplayType === 'label' && anno.label_index >= 0) {
    color = getColor(anno.label_index);
  }
  return { polygons, color };
};

const uint32ToHex = (uint32Value) => {
  if (typeof uint32Value !== 'number' || uint32Value < 0 || uint32Value > 0xFFFFFFFF || !Number.isInteger(uint32Value)) {
    throw new TypeError('Input must be a valid 32-bit unsigned integer');
  }
  return `#${uint32Value.toString(16).padStart(8, '0').toUpperCase()}`;
};

const parseColor = (color) => {
  if (typeof color === 'string') {
    let c = color.slice(1);
    if (c.length === 6) c += 'FF';
    if (c.length === 8) { c = c.slice(0, -2) + 'FF'; }
    return c;
  } else if (typeof color === 'number') {
    return uint32ToHex(color);
  }
  return null;
};

const downloadImage = async () => {
  const url = `/v3/enterprise/image/${props.imageObj.id}`;
  const headers = new Headers({});
  try {
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = props.imageObj.name || props.imageObj.id || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading the image:', error);
  }
};
</script>

<style lang="scss" scoped>
.image-card {
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid gray;
  overflow: hidden;
  width: max-content;

  @include themify() {
    background: themed('image-background-fill');
    color: themed('text-color');
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
    @include themify() {
      outline: 1px solid themed('card-list-hover-outline');
    }
  }

   &__checkbox {
    position: absolute;
    top: 2px;
    left: 2px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
    padding: 0;
    height: 30px;
    overflow: hidden;

    :deep(.v-input) {
      margin-top: -5px;
      margin-left: -1px;

    }
  }

  &__menu {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 100%;
  }

  &__menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__video-icon {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.65);
    border-radius: 100%;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.5);
    color: var(--icon-color-primary);
  }

  &__name-overlay {
    position: absolute;
    bottom: 2px;
    left: 2px;
    z-index: 15;
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    background: rgba(255, 235, 59, 0.85);
    color: #333;
    overflow: hidden;
    width: fit-content;
    max-width: calc(100% - 4px);
  }

  &__name-text {
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__labels {
    flex: 0 0 auto;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 6px;
    padding: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    max-height: 75px;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    @include themify() {
      background: themed('image-details-background-fill');
    }
  }

  &__tags {
    flex: 0 0 32px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    overflow: hidden;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    @include themify() {
      background: themed('image-details-background-fill');
    }
  }

  &__section-title {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    overflow: hidden;
  }

  &__label-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    min-width: 24px;
    padding: 2px 8px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    font-weight: 500;
    border: 2px solid;
    flex: 0 0 auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.image-wrapper {
    position: relative;
    height: 160px;
    width: fit-content;
    overflow: hidden;
}

.image-canvas {

}

</style>
